/**
 * Qui parle en premier — chunk 9.BH.1.
 *
 * ## Deux sources qui ont raison en même temps
 *
 * Le dossier `test resume order` a rendu le problème visible : ses deux
 * assureurs avaient répondu, et la page annonçait **« Prochaine action :
 * définir ce que ce dossier doit réunir »**.
 *
 * Aucune des deux sources n'avait tort.
 *
 *   - `operational_state` (0082) dit **ce que le dossier attend** : ici, plus
 *     personne — il y a de quoi comparer.
 *   - `completeness.next_action` dit **où en est la préparation** : ici, aucune
 *     exigence n'a jamais été déclarée, donc rien n'a été vérifié. C'est vrai, et
 *     `buildVerdict` est une fonction pure sur les exigences : elle ne connaît ni
 *     les demandes, ni les réponses, et ne doit pas les connaître.
 *
 * Ce qui manquait n'était pas une correction, c'était **la phrase qui dit
 * laquelle on écoute**.
 *
 * ## Pourquoi ici et pas ailleurs
 *
 * Pas dans `completeness` : lui apprendre les demandes casserait une
 * responsabilité que son en-tête délimite explicitement (« aucune règle de
 * courtage ici »).
 *
 * Pas dans `app.project_operational` : composer exigerait de lire les exigences,
 * soit deux requêtes de plus **par ligne** de liste — et le conflit n'existe pas
 * sur la liste, qui ne porte pas `next_action`.
 *
 * Pas côté web : la même règle serait recopiée sur le dossier, le tableau de
 * bord et la fiche client, et resterait invisible au MCP.
 *
 * Une fonction pure, donc, testée une fois, disponible à tout consommateur
 * TypeScript. Les deux sources restent exactement ce qu'elles sont.
 */

import type { NextAction } from "../schemas/project-requirements.js";

/** Les états dérivés du travail de demande (migration 0082). */
export type EtatOperationnel =
  | "to_prepare"
  | "preparing"
  | "awaiting_insurers"
  | "partial_responses"
  | "ready_to_compare";

/** Les actions que la complétude sait recommander. */
export type ActionCompletude = NextAction;

/**
 * Les états où le travail a DÉPASSÉ la préparation.
 *
 * Une demande partie ne se déprépare pas : quoi qu'il manque au dossier, ce que
 * le conseiller doit faire maintenant se joue du côté des assureurs.
 */
const ENGAGES: ReadonlySet<EtatOperationnel> = new Set([
  "awaiting_insurers",
  "partial_responses",
  "ready_to_compare",
]);

export type ActionPrincipale = {
  /** Quelle source porte l'action principale. */
  voix: "operationnelle" | "completude";
  /** L'état opérationnel, quand c'est lui qui parle. */
  etat: EtatOperationnel | null;
  /** L'action de complétude, quand c'est elle qui parle. */
  action: ActionCompletude | null;
  /**
   * La lacune de complétude reléguée au second plan — jamais effacée.
   *
   * Une exigence manquante reste une exigence manquante ; elle cesse seulement
   * d'être ce qu'on doit faire à l'instant. L'effacer ferait disparaître du
   * dossier une information que personne ne redemanderait.
   */
  avertissement: ActionCompletude | null;
};

/**
 * Compose les deux voix sans changer ni l'une ni l'autre.
 *
 * `to_prepare` et `preparing` laissent la complétude guider : c'est précisément
 * de préparation qu'il s'agit, et elle en sait plus que l'état du workflow.
 *
 * Les trois autres états sont **engagés** : l'action opérationnelle passe
 * devant, et la complétude devient un avertissement.
 */
export function actionPrincipale(entree: {
  etatOperationnel: EtatOperationnel | null | undefined;
  actionCompletude: ActionCompletude | null | undefined;
}): ActionPrincipale {
  const etat = entree.etatOperationnel ?? null;
  const action = entree.actionCompletude ?? null;

  if (etat !== null && ENGAGES.has(etat)) {
    return { voix: "operationnelle", etat, action: null, avertissement: action };
  }

  // Sans état opérationnel — un dossier dont la projection n'a rien rendu — la
  // complétude reste la seule voix disponible. Se taire serait pire.
  return { voix: "completude", etat: null, action, avertissement: null };
}
